import { useEffect, useRef, useState } from "react";

interface UseDebounceResult<T> {
  debouncedValue: T;
  isPending: boolean;
}

/**
 * Simple and reliable debounce hook with pending state tracking.
 *
 * @param value - The value to debounce
 * @param delay - Debounce delay in milliseconds (default: 300ms)
 * @returns Object containing debouncedValue and isPending state
 */
export const useDebounce = <T>(value: T, delay = 300): UseDebounceResult<T> => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // isPending is true when value differs from debouncedValue
  const isPending = value !== debouncedValue;

  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // For empty strings, update immediately without debounce
    if (value === "") {
      setDebouncedValue(value);
      return;
    }

    // Set up debounce timer
    timerRef.current = setTimeout(() => {
      setDebouncedValue(value);
      timerRef.current = null;
    }, delay);

    // Cleanup on unmount or when value/delay changes
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [value, delay]);

  return {
    debouncedValue,
    isPending,
  };
};

export default useDebounce;
